'use client';

import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {Button} from '@/matic/components/ui/button';
import {getMfaApi} from '../../api/mfa-api';
import {useApiToken} from '@/matic/hooks/useApiClient';

const RESEND_COOLDOWN_SECONDS = 60;

type MfaVerificationProps = {
  onVerified: (mfaToken: string) => void;
};

export function MfaVerification({onVerified}: MfaVerificationProps) {
  const [code, setCode] = useState('');
  const [error, setError] = useState('');
  const [isVerifying, setIsVerifying] = useState(false);
  const [isSending, setIsSending] = useState(false);
  const [cooldown, setCooldown] = useState(0);
  const [codeSent, setCodeSent] = useState(false);
  const token = useApiToken();
  const inputRef = useRef<HTMLInputElement>(null);

  const api = useMemo(() => getMfaApi(token), [token]);

  const sendCode = useCallback(async () => {
    setIsSending(true);
    setError('');
    try {
      await api.sendMfaCode();
      setCodeSent(true);
      setCooldown(RESEND_COOLDOWN_SECONDS);
    } catch {
      setError('Failed to send verification code. Please try again.');
    } finally {
      setIsSending(false);
    }
  }, [api]);

  // Send code on mount
  useEffect(() => {
    void sendCode();
  }, []);

  // Cooldown timer
  useEffect(() => {
    if (cooldown <= 0) return;
    const timer = setInterval(() => {
      setCooldown((prev) => prev - 1);
    }, 1000);
    return () => {
      clearInterval(timer);
    };
  }, [cooldown]);

  // Auto-focus input
  useEffect(() => {
    if (codeSent && inputRef.current) {
      inputRef.current.focus();
    }
  }, [codeSent]);

  const handleVerify = async () => {
    if (code.length !== 6) return;
    setIsVerifying(true);
    setError('');
    try {
      const result = await api.verifyMfaCode(code);
      if (result.verified) {
        onVerified(result.mfaToken);
      }
    } catch (error_: unknown) {
      const message =
        error_ instanceof Error ? error_.message : 'Verification failed';
      // Extract meaningful error from API response
      if (message.includes('expired')) {
        setError('Verification code has expired. Please request a new code.');
      } else if (message.includes('attempts')) {
        setError('Maximum attempts exceeded. Please request a new code.');
      } else if (message.includes('Invalid') || message.includes('invalid')) {
        setError('Invalid verification code. Please try again.');
      } else {
        setError('Verification failed. Please try again.');
      }
    } finally {
      setIsVerifying(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      void handleVerify();
    }
  };

  return (
    <div
      className="flex min-h-screen items-center justify-center"
      style={{backgroundColor: '#111827'}}
    >
      <div className="mx-auto w-full max-w-md space-y-6 rounded-lg border border-gray-700 bg-gray-800 p-8 shadow-lg">
        <div className="text-center">
          <h2 className="text-2xl font-semibold text-white">
            Verify Your Identity
          </h2>
          <p className="mt-2 text-sm text-gray-400">
            {codeSent
              ? 'A verification code has been sent to your email.'
              : 'Sending verification code...'}
          </p>
        </div>

        <div className="space-y-4">
          <div>
            <label
              htmlFor="mfa-code"
              className="block text-sm font-medium text-gray-300"
            >
              Verification Code
            </label>
            <input
              ref={inputRef}
              id="mfa-code"
              type="text"
              inputMode="numeric"
              autoComplete="one-time-code"
              maxLength={6}
              value={code}
              onChange={(e) => {
                const val = e.target.value.replaceAll(/\D/g, '');
                setCode(val);
              }}
              onKeyDown={handleKeyDown}
              placeholder="000000"
              className="mt-1 block w-full rounded-md border border-gray-600 bg-gray-700 px-3 py-2 text-center text-2xl tracking-[0.5em] text-white placeholder-gray-500 shadow-sm focus:border-[#FF6B35] focus:outline-none focus:ring-1 focus:ring-[#FF6B35]"
              disabled={isVerifying || isSending}
            />
          </div>

          {error && (
            <p className="text-sm text-red-400" role="alert">
              {error}
            </p>
          )}

          <Button
            type="button"
            onClick={handleVerify}
            loading={isVerifying}
            disabled={code.length !== 6 || isVerifying}
            variant="primary"
            size="lg"
            className="w-full"
          >
            {isVerifying ? 'Verifying...' : 'Verify'}
          </Button>

          <div className="text-center">
            {cooldown > 0 ? (
              <p className="text-sm text-gray-500">
                Resend code in {cooldown}s
              </p>
            ) : (
              <button
                type="button"
                onClick={sendCode}
                disabled={isSending}
                className="text-sm font-medium text-[#FF6B35] hover:text-[#ff8a5c] disabled:opacity-50"
              >
                {isSending ? 'Sending...' : 'Resend Code'}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
